import {
  Award,
  Calendar,
  CheckCircle,
  Download,
  Edit,
  FileText,
  MapPin,
  Package,
  Plus,
  QrCode,
  Shield,
  Trash2,
  Upload,
  User,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { api } from "../../services/api";
import type {
  FHIRImmunizationBundle,
  Immunization,
  InternationalCertificate,
} from "../../types/immunization";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { useAuthStore } from "../../stores/authStore";
import { useI18nStore } from "../../stores/i18nStore";

interface ImmunizationFormData {
  vaccineName: string;
  vaccineCode: string;
  manufacturer: string;
  lotNumber: string;
  doseNumber: number | string;
  totalDoses: number | string;
  administeredDate: string;
  expirationDate: string;
  nextDueDate: string;
  location: string;
  administrator: string;
  requiredForCountries: string[];
}

const ImmunizationsPage: React.FC = () => {
  const { token } = useAuthStore();
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  // Messages raised inside async handlers read the language current at that
  // moment rather than the one captured by the closure.
  const msg = () => useI18nStore.getState().t;

  const [immunizations, setImmunizations] = useState<Immunization[]>([]);
  const [passportNumber, setPassportNumber] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ImmunizationFormData>({
    vaccineName: "",
    vaccineCode: "",
    manufacturer: "",
    lotNumber: "",
    doseNumber: "",
    totalDoses: "",
    administeredDate: "",
    expirationDate: "",
    nextDueDate: "",
    location: "",
    administrator: "",
    requiredForCountries: [],
  });

  // Certificate upload states
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [selectedImmunizationId, setSelectedImmunizationId] = useState<
    string | null
  >(null);
  const [uploadingCertificate, setUploadingCertificate] = useState(false);

  const [showFHIRModal, setShowFHIRModal] = useState(false);
  const [fhirData, setFhirData] = useState<FHIRImmunizationBundle | null>(null);
  const [showInternationalCert, setShowInternationalCert] = useState(false);
  const [internationalCertData, setInternationalCertData] =
    useState<InternationalCertificate | null>(null);

  // Load immunizations
  //
  // Wrapped in useCallback so its identity is stable. It was previously a plain
  // function listed in the effect's dependency array, so a fresh reference on
  // every render re-fired the effect, which called setLoading(true) and
  // refetched — an infinite loop that left the page stuck on its spinner.
  const loadImmunizations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/immunizations");
      if (response.data?.success) {
        setImmunizations(response.data.data.immunizations);
      }
    } catch (err: any) {
      setError(err instanceof Error ? err.message : msg().immuLoadFailed);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadImmunizations();
  }, [token, loadImmunizations]);

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        doseNumber: formData.doseNumber
          ? parseInt(formData.doseNumber.toString())
          : undefined,
        totalDoses: formData.totalDoses
          ? parseInt(formData.totalDoses.toString())
          : undefined,
        requiredForCountries: formData.requiredForCountries.filter((c) =>
          c.trim(),
        ),
      };

      const response = editingId
        ? await api.put(`/immunizations/${editingId}`, submitData)
        : await api.post("/immunizations", submitData);

      if (response.data?.success) {
        await loadImmunizations();
        resetForm();
      }
    } catch (err: any) {
      setError(err instanceof Error ? err.message : msg().immuSaveFailed);
    } finally {
      setLoading(false);
    }
  };

  // Delete immunization
  const handleDelete = async (id: string) => {
    if (!confirm(t.immuDeleteConfirm)) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/immunizations/${id}`);
      await loadImmunizations();
    } catch (err: any) {
      setError(err instanceof Error ? err.message : msg().immuDeleteFailed);
    } finally {
      setLoading(false);
    }
  };

  // Edit immunization
  const handleEdit = (immunization: Immunization) => {
    setEditingId(immunization.id);
    setFormData({
      vaccineName: immunization.vaccineName,
      vaccineCode: immunization.vaccineCode || "",
      manufacturer: immunization.manufacturer || "",
      lotNumber: immunization.lotNumber || "",
      doseNumber: immunization.doseNumber || "",
      totalDoses: immunization.totalDoses || "",
      administeredDate: immunization.administeredDate?.split("T")[0] || "",
      expirationDate: immunization.expirationDate?.split("T")[0] || "",
      nextDueDate: immunization.nextDueDate?.split("T")[0] || "",
      location: immunization.location || "",
      administrator: immunization.administrator || "",
      requiredForCountries: immunization.requiredForCountries || [],
    });
    setShowForm(true);
  };

  // Reset form
  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      vaccineName: "",
      vaccineCode: "",
      manufacturer: "",
      lotNumber: "",
      doseNumber: "",
      totalDoses: "",
      administeredDate: "",
      expirationDate: "",
      nextDueDate: "",
      location: "",
      administrator: "",
      requiredForCountries: [],
    });
  };

  // Format date for display in the app language
  const formatDate = (dateString: string) => fmt.date(dateString);
  const orNA = (value: string | undefined | null, style?: "time") =>
    value
      ? style === "time"
        ? fmt.dateTime(value)
        : fmt.date(value)
      : t.immuNotAvailable;
  const sexLabel = (sex: string | undefined) => {
    switch ((sex ?? "").toLowerCase()) {
      case "male":
      case "m":
        return t.immuSexMale;
      case "female":
      case "f":
        return t.immuSexFemale;
      case "other":
        return t.immuSexOther;
      case "unknown":
        return t.immuSexUnknown;
      default:
        return sex ?? "";
    }
  };

  // Load FHIR data
  const loadFHIRData = async () => {
    try {
      setLoading(true);
      const response = await api.get("/immunizations/fhir");
      if (response.data?.success) {
        setFhirData(response.data.data);
        setShowFHIRModal(true);
      }
    } catch (err: any) {
      setError(err instanceof Error ? err.message : msg().immuFhirLoadFailed);
    } finally {
      setLoading(false);
    }
  };

  // Load International Certificate
  const loadInternationalCertificate = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        "/immunizations/certificate/international",
      );
      if (response.data?.success) {
        const certificate = response.data.data.certificate;
        setInternationalCertData(certificate);
        setPassportNumber(certificate.passportNumber || "");
        setShowInternationalCert(true);
      }
    } catch (err: any) {
      setError(err instanceof Error ? err.message : msg().immuCertLoadFailed);
    } finally {
      setLoading(false);
    }
  };

  // Upload certificate
  const uploadCertificate = async (
    immunizationId: string,
    formData: FormData,
  ) => {
    try {
      setUploadingCertificate(true);
      const response = await api.post(
        `/immunizations/${immunizationId}/certificate`,
        formData,
      );

      if (response.data?.success) {
        // Close modal and refresh immunizations
        setShowCertificateModal(false);
        setSelectedImmunizationId(null);
        loadImmunizations();
        // Show success message
        setError(null);
      }
    } catch (err: any) {
      const serverError = err?.response?.data?.error;
      setError(
        serverError?.code === "SUBSCRIPTION_REQUIRED"
          ? serverError.message
          : (serverError?.message ??
              (err instanceof Error ? err.message : msg().immuUploadFailed)),
      );
    } finally {
      setUploadingCertificate(false);
    }
  };

  // Download certificate
  const handleCertificateDownload = async (immunizationId: string) => {
    try {
      const response = await api.get(
        `/immunizations/${immunizationId}/certificate`,
        { responseType: "blob" },
      );

      const blob = response.data as Blob;
      const extension = blob.type?.split("/").pop() || "pdf";

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificate-${immunizationId}.${extension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setError(msg().immuNoCertificate);
        return;
      }
      setError(err instanceof Error ? err.message : msg().immuDownloadFailed);
    }
  };

  if (loading && immunizations.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t.immunizations}
          </h1>
          <p className="text-gray-600">{t.manageImmunizations}</p>
          {passportNumber && (
            <div className="flex items-center mt-2 text-sm text-primary-600">
              <Shield className="w-4 h-4 mr-2" />
              <span className="font-medium">{t.passportNumber}:</span>
              <code className="ml-2 px-2 py-1 bg-primary-50 rounded font-mono">
                {passportNumber}
              </code>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={loadFHIRData}
            className="inline-flex items-center whitespace-nowrap px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            title={t.immuDigitalRecordTitle}
          >
            <FileText className="w-4 h-4 mr-2" />
            {t.immuDigitalRecord}
          </button>
          <button
            onClick={loadInternationalCertificate}
            className="inline-flex items-center whitespace-nowrap px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            title={t.internationalCertificate}
          >
            <Award className="w-4 h-4 mr-2" />
            {t.internationalCertificate}
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center whitespace-nowrap px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t.newImmunization}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">
                {editingId ? t.editImmunization : t.newImmunization}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
                aria-label={t.close}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Vaccine Name */}
              <div>
                <label
                  htmlFor="immunization-vaccine-name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t.immuVaccineName} *
                </label>
                <input
                  id="immunization-vaccine-name"
                  type="text"
                  required
                  value={formData.vaccineName}
                  onChange={(e) =>
                    setFormData({ ...formData, vaccineName: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder={t.immuVaccineNamePlaceholder}
                />
              </div>

              {/* Row 1: Vaccine Code and Manufacturer */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="immunization-vaccine-code"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.vaccineCode}
                  </label>
                  <input
                    id="immunization-vaccine-code"
                    type="text"
                    value={formData.vaccineCode}
                    onChange={(e) =>
                      setFormData({ ...formData, vaccineCode: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t.immuVaccineCodePlaceholder}
                  />
                </div>
                <div>
                  <label
                    htmlFor="immunization-manufacturer"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.manufacturer}
                  </label>
                  <input
                    id="immunization-manufacturer"
                    type="text"
                    value={formData.manufacturer}
                    onChange={(e) =>
                      setFormData({ ...formData, manufacturer: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t.immuManufacturerPlaceholder}
                  />
                </div>
              </div>

              {/* Row 2: Lot Number and Doses */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label
                    htmlFor="immunization-lot-number"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.lotNumber}
                  </label>
                  <input
                    id="immunization-lot-number"
                    type="text"
                    value={formData.lotNumber}
                    onChange={(e) =>
                      setFormData({ ...formData, lotNumber: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t.immuLotPlaceholder}
                  />
                </div>
                <div>
                  <label
                    htmlFor="immunization-dose-number"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.immuDoseNumber}
                  </label>
                  <input
                    id="immunization-dose-number"
                    type="number"
                    min="1"
                    value={formData.doseNumber}
                    onChange={(e) =>
                      setFormData({ ...formData, doseNumber: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="1"
                  />
                </div>
                <div>
                  <label
                    htmlFor="immunization-total-doses"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.immuTotalDoses}
                  </label>
                  <input
                    id="immunization-total-doses"
                    type="number"
                    min="1"
                    value={formData.totalDoses}
                    onChange={(e) =>
                      setFormData({ ...formData, totalDoses: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="2"
                  />
                </div>
              </div>

              {/* Row 3: Dates */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label
                    htmlFor="immunization-admin-date"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.immuAdminDate} *
                  </label>
                  <input
                    id="immunization-admin-date"
                    type="date"
                    required
                    value={formData.administeredDate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        administeredDate: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label
                    htmlFor="immunization-expiration-date"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.immuExpirationDate}
                  </label>
                  <input
                    id="immunization-expiration-date"
                    type="date"
                    value={formData.expirationDate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        expirationDate: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label
                    htmlFor="immunization-next-due"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.immuNextDueDate}
                  </label>
                  <input
                    id="immunization-next-due"
                    type="date"
                    value={formData.nextDueDate}
                    onChange={(e) =>
                      setFormData({ ...formData, nextDueDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Row 4: Location and Administrator */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="immunization-location"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.immuAdminLocation}
                  </label>
                  <input
                    id="immunization-location"
                    type="text"
                    value={formData.location}
                    onChange={(e) =>
                      setFormData({ ...formData, location: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t.immuLocationPlaceholder}
                  />
                </div>
                <div>
                  <label
                    htmlFor="immunization-administrator"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.immuAdministeredBy}
                  </label>
                  <input
                    id="immunization-administrator"
                    type="text"
                    value={formData.administrator}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        administrator: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t.immuAdministratorPlaceholder}
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex items-center justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t.cancel}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? t.saving : t.save}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Immunizations List */}
      <div className="space-y-4">
        {immunizations.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t.immuEmptyTitle}
            </h3>
            <p className="text-gray-600 mb-4">{t.immuEmptyHint}</p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t.immuAddFirst}
            </button>
          </div>
        ) : (
          immunizations.map((immunization) => (
            <div
              key={immunization.id}
              className="bg-white rounded-lg border border-gray-200 p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {immunization.vaccineName}
                    </h3>
                    {immunization.manufacturer && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                        <Package className="w-3 h-3 mr-1" />
                        {immunization.manufacturer}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>{formatDate(immunization.administeredDate)}</span>
                    </div>

                    {immunization.location && (
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        <span>{immunization.location}</span>
                      </div>
                    )}

                    {immunization.administrator && (
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-2" />
                        <span>{immunization.administrator}</span>
                      </div>
                    )}

                    {immunization.doseNumber && immunization.totalDoses && (
                      <div className="flex items-center">
                        <span>
                          {t.immuDoseOf
                            .replace("{dose}", String(immunization.doseNumber))
                            .replace(
                              "{total}",
                              String(immunization.totalDoses),
                            )}
                        </span>
                      </div>
                    )}
                  </div>

                  {immunization.lotNumber && (
                    <div className="mt-2 text-xs text-gray-500">
                      {t.immuLot.replace("{lot}", immunization.lotNumber)}
                    </div>
                  )}

                  {immunization.nextDueDate && (
                    <div className="mt-2 text-sm">
                      <span className="text-amber-600">
                        {t.immuNextDue.replace(
                          "{date}",
                          formatDate(immunization.nextDueDate),
                        )}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleCertificateDownload(immunization.id)}
                    className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title={t.immuDownloadCert}
                    aria-label={t.immuDownloadCert}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedImmunizationId(immunization.id);
                      setShowCertificateModal(true);
                    }}
                    className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                    title={t.immuUploadCert}
                    aria-label={t.immuUploadCert}
                  >
                    <Upload className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(immunization)}
                    className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                    title={t.edit}
                    aria-label={t.edit}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(immunization.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title={t.delete}
                    aria-label={t.delete}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}

        {/* Certificate Upload Modal */}
        {showCertificateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">{t.immuUploadTitle}</h2>
                <button
                  onClick={() => {
                    setShowCertificateModal(false);
                    setSelectedImmunizationId(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label={t.close}
                >
                  ×
                </button>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target as HTMLFormElement);
                  uploadCertificate(selectedImmunizationId!, formData);
                }}
              >
                <div className="mb-4">
                  <label
                    htmlFor="immunization-certificate-upload"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t.immuCertFileLabel}
                  </label>
                  <input
                    id="immunization-certificate-upload"
                    type="file"
                    name="certificate"
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCertificateModal(false);
                      setSelectedImmunizationId(null);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                  >
                    {t.cancel}
                  </button>
                  <button
                    type="submit"
                    disabled={uploadingCertificate}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {uploadingCertificate ? t.immuUploading : t.immuUpload}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* FHIR Data Modal */}
        {showFHIRModal && fhirData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">
                  {t.immuFhirBundleTitle}
                </h2>
                <button
                  onClick={() => {
                    setShowFHIRModal(false);
                    setFhirData(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label={t.close}
                >
                  ×
                </button>
              </div>
              <div className="overflow-y-auto max-h-96">
                <pre className="bg-gray-100 p-4 rounded-lg text-xs">
                  {JSON.stringify(fhirData, null, 2)}
                </pre>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(
                      JSON.stringify(fhirData, null, 2),
                    );
                    alert(t.immuRecordCopied);
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {t.immuCopyToClipboard}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* International Certificate Modal */}
        {showInternationalCert && internationalCertData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">
                  {t.internationalVaccinationCertificate}
                </h2>
                <button
                  onClick={() => {
                    setShowInternationalCert(false);
                    setInternationalCertData(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label={t.close}
                >
                  ×
                </button>
              </div>
              <div className="overflow-y-auto max-h-[75vh]">
                <div className="bg-gradient-to-br from-primary-50 to-green-50 p-8 rounded-lg border-2 border-primary-200 shadow-lg">
                  {/* Header with Official Seal */}
                  <div className="text-center mb-8 border-b-2 border-primary-300 pb-6">
                    <div className="flex items-center justify-center mb-4">
                      <Shield className="w-12 h-12 text-primary-600 mr-4" />
                      <div>
                        <h3 className="text-3xl font-bold text-primary-800">
                          {internationalCertData.certificateType}
                        </h3>
                        <p className="text-lg text-primary-600 font-semibold">
                          {t.certificateNumber}:{" "}
                          {internationalCertData.certificateNumber}
                        </p>
                      </div>
                      <QrCode className="w-12 h-12 text-primary-600 ml-4" />
                    </div>

                    {/* QR Code for Validation */}
                    <div className="keep-light bg-white p-4 rounded-lg inline-block border-2 border-primary-300">
                      <QRCode
                        value={JSON.stringify({
                          certificateId:
                            internationalCertData.certificateNumber,
                          validationUrl: `https://healthpass.plus/certverification/${internationalCertData.certificateNumber.toLowerCase()}`,
                          issuer: internationalCertData.issuedBy,
                          issuedDate: internationalCertData.issuedDate,
                        })}
                        size={120}
                        level="H"
                      />
                      <p className="text-xs text-gray-600 mt-2 text-center">
                        {t.qrCodeValidation}
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-8 mb-8">
                    {/* Patient Information */}
                    <div className="bg-white p-6 rounded-lg border border-primary-200">
                      <h4 className="font-bold text-primary-800 mb-4 flex items-center">
                        <User className="w-5 h-5 mr-2" />
                        {t.patientInformation}
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p>
                          <strong>{t.immuName}:</strong>{" "}
                          {internationalCertData.patient?.name}
                        </p>
                        <p>
                          <strong>{t.immuDateOfBirth}:</strong>{" "}
                          {orNA(internationalCertData.patient?.dateOfBirth)}
                        </p>
                        <p>
                          <strong>{t.nationality}:</strong>{" "}
                          {internationalCertData.patient?.nationality}
                        </p>
                        <p>
                          <strong>{t.immuSex}:</strong>{" "}
                          {sexLabel(internationalCertData.patient?.sex)}
                        </p>
                        <p>
                          <strong>{t.healthPassDocumentId}:</strong>{" "}
                          {internationalCertData.patient?.healthPassDocumentId}
                        </p>
                      </div>
                    </div>

                    {/* Certificate Information */}
                    <div className="bg-white p-6 rounded-lg border border-green-200">
                      <h4 className="font-bold text-green-800 mb-4 flex items-center">
                        <Award className="w-5 h-5 mr-2" />
                        {t.certificateInformation}
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p>
                          <strong>{t.passportNumber}:</strong>{" "}
                          <code className="px-2 py-1 bg-primary-50 rounded font-mono text-primary-700">
                            {internationalCertData.passportNumber ||
                              t.immuNotAvailable}
                          </code>
                        </p>
                        <p>
                          <strong>{t.certificateNumber}:</strong>{" "}
                          {internationalCertData.certificateNumber}
                        </p>
                        <p>
                          <strong>{t.issuedBy}:</strong>{" "}
                          {internationalCertData.issuedBy}
                        </p>
                        <p>
                          <strong>{t.issuedDate}:</strong>{" "}
                          {orNA(internationalCertData.issuedDate)}
                        </p>
                        <p>
                          <strong>{t.validFrom}:</strong>{" "}
                          {orNA(internationalCertData.validFrom)}
                        </p>
                        <p>
                          <strong>{t.certificateValidUntil}:</strong>{" "}
                          {orNA(internationalCertData.validUntil)}
                        </p>
                      </div>
                    </div>

                    {/* Digital Signature */}
                    <div className="bg-white p-6 rounded-lg border border-purple-200">
                      <h4 className="font-bold text-purple-800 mb-4 flex items-center">
                        <CheckCircle className="w-5 h-5 mr-2" />
                        {t.digitalSignature}
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p>
                          <strong>{t.immuAlgorithm}:</strong>{" "}
                          <code className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">
                            {internationalCertData.digitalSignature
                              ?.algorithm || "SHA256withRSA"}
                          </code>
                        </p>
                        <p>
                          <strong>{t.issuerId}:</strong>{" "}
                          <span className="font-mono text-primary-600">
                            {internationalCertData.digitalSignature?.issuerId ||
                              "DE-HealthPass-2025"}
                          </span>
                        </p>
                        <p>
                          <strong>{t.immuSignature}:</strong>{" "}
                          <span className="font-mono text-xs bg-gray-50 px-2 py-1 rounded block mt-1">
                            {internationalCertData.digitalSignature
                              ?.signature || t.immuNotAvailable}
                          </span>
                        </p>
                        <p>
                          <strong>{t.publicKeyFingerprint}:</strong>
                        </p>
                        <p className="font-mono text-xs bg-gray-50 px-2 py-1 rounded mt-1 break-all">
                          {internationalCertData.digitalSignature
                            ?.publicKeyFingerprint || t.immuNotAvailable}
                        </p>
                        <p>
                          <strong>{t.immuVerified}:</strong>
                          <span className="ml-2 inline-flex items-center">
                            {internationalCertData.digitalSignature ? (
                              <>
                                <CheckCircle className="w-4 h-4 text-green-600 mr-1" />
                                <span className="text-green-600 font-semibold">
                                  {t.immuSignedBy.replace(
                                    "{issuer}",
                                    internationalCertData.issuedBy ?? "",
                                  )}
                                </span>
                              </>
                            ) : (
                              <span className="text-amber-600 font-semibold">
                                {t.immuUnsigned}
                              </span>
                            )}
                          </span>
                        </p>
                        <p>
                          <strong>{t.signatureTimestamp}:</strong>{" "}
                          <span className="font-medium">
                            {orNA(
                              internationalCertData.digitalSignature?.timestamp,
                              "time",
                            )}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Vaccinations */}
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h4 className="font-bold text-gray-800 mb-4 flex items-center">
                      <Package className="w-5 h-5 mr-2" />
                      {t.vaccinations}
                    </h4>
                    <div className="space-y-4">
                      {internationalCertData.vaccinations.map((vacc, index) => (
                        <div
                          key={index}
                          className="bg-gray-50 p-4 rounded-lg border"
                        >
                          <div className="grid md:grid-cols-4 gap-4">
                            <div>
                              <p className="font-semibold text-primary-800">
                                {vacc.vaccine}
                              </p>
                              <p className="text-sm text-gray-600">
                                {t.manufacturer}: {vacc.manufacturer}
                              </p>
                              <p className="text-sm text-gray-600">
                                {t.vaccineCode}: {vacc.vaccineCode}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm">
                                <strong>{t.immuDate}:</strong>{" "}
                                {formatDate(vacc.dateOfVaccination)}
                              </p>
                              <p className="text-sm">
                                <strong>{t.location}:</strong>{" "}
                                {vacc.professionalStamp}
                              </p>
                              <p className="text-sm">
                                <strong>{t.immuAdministeredBy}:</strong>{" "}
                                {vacc.signature}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm">
                                <strong>{t.certificateNumber}:</strong>{" "}
                                {vacc.certificateNumber}
                              </p>
                              {vacc.batchLotNumber && (
                                <p className="text-sm">
                                  <strong>{t.lotNumber}:</strong>{" "}
                                  {vacc.batchLotNumber}
                                </p>
                              )}
                              <p className="text-sm">
                                <strong>{t.country}:</strong>{" "}
                                {vacc.countryOfVaccination}
                              </p>
                            </div>
                            <div>
                              {vacc.doseNumber && vacc.totalDoses && (
                                <p className="text-sm">
                                  <strong>{t.dosesCompleted}:</strong>{" "}
                                  {vacc.doseNumber} {t.of} {vacc.totalDoses}
                                </p>
                              )}
                              <div className="flex items-center mt-2">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-1" />
                                <span className="text-xs text-green-600 font-semibold">
                                  {t.immuVerified}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end mt-6 space-x-3">
                <button
                  onClick={() => {
                    const printWindow = window.open("", "_blank");
                    if (printWindow) {
                      printWindow.document.write(`
                        <html>
                          <head>
                            <title>${t.internationalVaccinationCertificate}</title>
                            <style>
                              body { font-family: Arial, sans-serif; margin: 20px; }
                              .certificate { max-width: 800px; margin: 0 auto; }
                            </style>
                          </head>
                          <body>
                            <div class="certificate">
                              ${document.querySelector(".bg-gradient-to-br")?.innerHTML}
                            </div>
                          </body>
                        </html>
                      `);
                      printWindow.print();
                    }
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {t.printCertificate}
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(
                      JSON.stringify(internationalCertData, null, 2),
                    );
                    alert(t.immuCertCopied);
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  {t.copyCertificateData}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImmunizationsPage;
